import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, LogIn, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND, LINKS } from "@/data/site";
import { useTranslation } from "@/i18n/LanguageContext";
import LanguageToggle from "@/components/LanguageToggle";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useTranslation();

  // ✅ 只改这里（加 SEO 页面）
  const navItems = [
    { to: "/", label: t.nav.home, key: "home" },
    { to: "/game", label: t.nav.games, key: "games" },
    { to: "/contact", label: t.nav.contact, key: "contact" },

 // 🔥 新增（SEO用）
    { to: "/casino-malaysia", label: "Casino", key: "casino" },
    { to: "/bonus", label: "Bonus", key: "bonus" },
    { to: "/u2-agent", label: "Agent", key: "agent" },
    { to: "/u2-slots", label: "Slots", key: "slots" },
    { to: "/u2-news", label: "News", key: "news" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#07070a]/85 backdrop-blur-xl border-b border-orange-500/20"
          : "bg-gradient-to-b from-[#07070a] to-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-[72px] flex items-center justify-between">
        {/* Logo */}
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-orange-500/40 blur-xl group-hover:bg-orange-500/70 transition" />
            <img
              src={BRAND.logo}
              alt="U2 Gaming logo"
              className="relative w-11 h-11 object-contain rounded-md"
            />
          </div>
          <div className="leading-none">
            <div className="font-display text-2xl tracking-wider text-orange-grad">
              {BRAND.name}
            </div>
            <div className="text-[10px] text-muted-foreground tracking-[0.25em] uppercase mt-0.5">
              {t.brand.subtitle}
            </div>
          </div>
        </Link>

        {/* Desktop */}
        <nav className="hidden lg:flex items-center gap-1" data-testid="navbar-desktop">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.key}`}
              className={({ isActive }) =>
                `px-5 py-2 rounded-full text-sm font-medium tracking-wide transition-all ${
                  isActive
                    ? "bg-orange-500/15 text-orange-300 ring-1 ring-orange-500/40"
                    : "text-zinc-300 hover:text-white hover:bg-white/5"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <LanguageToggle />
          <Button
            asChild
            variant="outline"
            data-testid="login-btn"
            className="rounded-full border-orange-500/40 bg-transparent text-orange-200 hover:bg-orange-500/10 hover:text-orange-100"
          >
            <a href={LINKS.login} target="_blank" rel="noreferrer">
              <LogIn className="w-4 h-4 mr-2" /> {t.nav.login}
            </a>
          </Button>
          <Button
            asChild
            data-testid="register-btn"
            className="rounded-full bg-gradient-to-b from-orange-400 to-orange-600 text-black font-semibold hover:from-orange-300 hover:to-orange-500 shine glow-orange-soft"
          >
            <a href={LINKS.register} target="_blank" rel="noreferrer">
              <UserPlus className="w-4 h-4 mr-2" /> {t.nav.register}
            </a>
          </Button>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((s) => !s)}
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-orange-500/30 text-orange-200"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden border-t border-orange-500/20 bg-[#0a0a0d]/95 backdrop-blur-xl"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-5 flex flex-col gap-3">
            <div className="flex justify-end pb-1">
              <LanguageToggle variant="mobile" />
            </div>
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.key}`}
                className={({ isActive }) =>
                  `px-4 py-2.5 rounded-lg text-sm font-medium ${
                    isActive
                      ? "bg-orange-500/15 text-orange-200 ring-1 ring-orange-500/40"
                      : "text-zinc-300 hover:bg-white/5"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button
                asChild
                variant="outline"
                data-testid="mobile-login-btn"
                className="rounded-full border-orange-500/40 bg-transparent text-orange-200"
              >
                <a href={LINKS.login} target="_blank" rel="noreferrer">
                  {t.nav.login}
                </a>
              </Button>
              <Button
                asChild
                data-testid="mobile-register-btn"
                className="rounded-full bg-gradient-to-b from-orange-400 to-orange-600 text-black"
              >
                <a href={LINKS.register} target="_blank" rel="noreferrer">
                  {t.nav.register}
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
